import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Prompt } from "react-router-dom";
import { Add, Cancel, Delete, Edit, Save } from "@material-ui/icons";
import { IpAddressDialog, IpAddressValues } from "./components/IpAddressDialog";
import { useIpAddressRestriction } from "./useIpAddressRestriction";

export const IpAddressRestrictionSettings: React.VFC = () => {
  const { draft, setDraft, status, modified, save, discard, retry } =
    useIpAddressRestriction();
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const busy = status === "saving";
  const addresses = draft?.ipAddresses ?? [];

  const submitAddress = (value: IpAddressValues) => {
    if (!draft) return;
    setDraft({
      ...draft,
      ipAddresses:
        editing === "new"
          ? [...addresses, value]
          : addresses.map((address, index) =>
              index === editing ? value : address
            ),
    });
    setEditing(null);
  };

  return (
    <Box>
      <Paper>
        <Box p={3}>
          <Typography variant="h5" component="h2">
            IPアドレス制限
          </Typography>
          {status === "loading" ? (
            <CircularProgress size={24} aria-label="IPアドレス制限を取得中" />
          ) : status === "failed" ? (
            <>
              <Typography>
                IPアドレス制限の設定を取得できませんでした。
              </Typography>
              <Button onClick={retry}>IPアドレス制限の再試行</Button>
            </>
          ) : (
            draft && (
              <>
                <Typography>
                  大臣AXクラウドへのアクセスを制限します。
                </Typography>
                <FormControlLabel
                  label="IPアドレス制限"
                  labelPlacement="start"
                  control={
                    <Switch
                      color="primary"
                      checked={draft.isIpAddressRestrictionEnabled ?? false}
                      disabled={busy}
                      onChange={(_, checked) =>
                        setDraft({
                          ...draft,
                          isIpAddressRestrictionEnabled: checked,
                        })
                      }
                    />
                  }
                />
                {draft.isIpAddressRestrictionEnabled && (
                  <>
                    <Typography>
                      大臣AXクラウドへの接続を許可するIPアドレスを追加してください。
                    </Typography>
                    <Typography variant="body2">
                      ※登録可能なIPアドレスは100個までです。（{addresses.length}個登録済み）
                    </Typography>
                    <Button
                      color="primary"
                      startIcon={<Add />}
                      disabled={busy || addresses.length >= 100}
                      onClick={() => setEditing("new")}
                    >
                      IPアドレスの追加
                    </Button>
                    <TableContainer>
                      <Table aria-label="接続を許可するIPアドレス">
                        <TableHead>
                          <TableRow>
                            <TableCell>IPアドレス</TableCell>
                            <TableCell>備考</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {addresses.map((address, index) => (
                            <TableRow key={address.ipAddress}>
                              <TableCell>{address.ipAddress}</TableCell>
                              <TableCell>{address.memo}</TableCell>
                              <TableCell align="right">
                                <Button
                                  startIcon={<Edit />}
                                  disabled={busy}
                                  onClick={() => setEditing(index)}
                                  aria-label={`${address.ipAddress}を編集`}
                                >
                                  編集
                                </Button>
                                <Button
                                  startIcon={<Delete />}
                                  disabled={busy}
                                  onClick={() => setDeleting(index)}
                                  aria-label={`${address.ipAddress}を削除`}
                                >
                                  削除
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )
          )}
          <Box mt={2}>
            <Button
              color="primary"
              variant="contained"
              startIcon={<Save />}
              disabled={status !== "ready" || !modified}
              onClick={save}
            >
              保存
            </Button>{" "}
            <Button
              startIcon={<Cancel />}
              disabled={status !== "ready" || !modified}
              onClick={discard}
            >
              破棄
            </Button>
            {busy && (
              <CircularProgress
                size={20}
                style={{ verticalAlign: "middle" }}
                aria-label="IPアドレス制限を保存中"
              />
            )}
          </Box>
        </Box>
      </Paper>
      <Prompt
        when={modified}
        message="保存されていない編集は破棄されます。"
      />
      {editing !== null && (
        <IpAddressDialog
          initial={
            editing === "new"
              ? undefined
              : {
                  ipAddress: addresses[editing].ipAddress!,
                  memo: addresses[editing].memo ?? "",
                }
          }
          otherAddresses={addresses
            .filter((_, index) => index !== editing)
            .map((x) => x.ipAddress!)}
          onSubmit={submitAddress}
          onClose={() => setEditing(null)}
        />
      )}
      <Dialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        aria-labelledby="delete-ip-title"
      >
        <DialogTitle id="delete-ip-title">IPアドレスの削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            「{deleting !== null && addresses[deleting]?.ipAddress}
            」を削除してもいいですか？
            <br />
            削除すると{deleting !== null && addresses[deleting]?.ipAddress}
            は大臣AXクラウドに接続できなくなります。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            onClick={() => {
              if (draft)
                setDraft({
                  ...draft,
                  ipAddresses: addresses.filter(
                    (_, index) => index !== deleting
                  ),
                });
              setDeleting(null);
            }}
          >
            削除
          </Button>
          <Button onClick={() => setDeleting(null)}>キャンセル</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
